import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Center, Checkbox, Group, Loader, Menu, Modal, Paper, Select, Stack, Table, Text, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { libraryApi, tenderApi, type ImportAction, type ImportResult, type ImportRun, type LibraryItem, type LibraryItemInput, type TenderItem } from '../../api';
import { GAEB_ACCEPT, IMPORT_OUTCOMES, IMPORT_TRIGGERS, LIBRARY_FIELDS, POSITION_TYPES, TEXT_PORTALS, formatDateTime } from '../../format';
import { FileDrop } from '../../ui/FileDrop';
import { useLazyCount } from '../../ui/PageScroll';
import { EditableText } from '../../ui/EditableText';
import { LibraryTable, type LibraryColumn } from '../../ui/LibraryTable';
import { errorText, useTenderMutation } from './usePackage';
import { depths } from './outline';
import { libraryItemFields } from './libraryItemDetail';
import { useCan } from '../../core/useMe';

/**
 * The office's text libraries (AP-78): several of them - "Büro", one per
 * manufacturer - each with titles and positions. Office-wide, not a
 * project's. PST fetches nothing; texts are typed or pasted in by hand, and
 * a position copied into a bill of quantities stays as it was copied.
 */
export function LibraryManager({ onClose, canEdit }: { onClose: () => void; canEdit: boolean }): JSX.Element {
  return (
    <Modal opened onClose={onClose} title="Textbibliotheken" size="90%">
      <LibraryManagerBody canEdit={canEdit} />
    </Modal>
  );
}

/** The libraries themselves - in the dialog from the bill of quantities and on the page Verwaltung › Bibliotheken. */
export function LibraryManagerBody({ canEdit }: { canEdit: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  // On a phone the list goes above the table instead of beside it.
  const narrow = useMediaQuery('(max-width: 48em)');
  const list = useQuery({ queryKey: ['libraries'], queryFn: libraryApi.list });
  const [selected, setSelected] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [importing, setImporting] = useState(false);
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['libraries'] });
  const create = useMutation({ mutationFn: () => libraryApi.create({ name: newName.trim() }), onSuccess: async ({ id }) => { setNewName(''); await refresh(); setSelected(id); } });
  const remove = useMutation({ mutationFn: (id: string) => libraryApi.remove(id), onSuccess: async () => { setSelected(null); await refresh(); } });

  useEffect(() => {
    if (!selected && list.data && list.data.length > 0) setSelected(list.data[0]!.id);
  }, [list.data, selected]);

  const current = list.data?.find((l) => l.id === selected);
  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Positionstexte des Büros für alle Projekte, etwa eigene Bürotexte oder je Hersteller eine Bibliothek. Texte werden von Hand eingetragen oder aus einer selbst heruntergeladenen GAEB-Datei eingelesen; automatische Abzüge aus Portalen sind im Aufbau (Einstellungen › Abzüge). Im Leistungsverzeichnis über „Aus Bibliothek …“ übernehmen – als Kopie, die sich später nicht mitändert.
      </Text>
      <Group gap="xs">
        {canEdit && <Button size="xs" variant="default" onClick={() => setImporting(true)}>GAEB-Datei einlesen …</Button>}
        <PortalLinks />
      </Group>
      {(create.error ?? remove.error) && <Alert color="red" py={6}>{errorText(create.error ?? remove.error)}</Alert>}
      <Group align="flex-start" gap="md" wrap={narrow ? 'wrap' : 'nowrap'}>
        <Stack gap="xs" w={narrow ? '100%' : 240} style={{ flexShrink: 0 }}>
          {list.isPending && <Loader size="sm" />}
          {list.data?.length === 0 && <Text size="sm" c="dimmed">Noch keine Bibliothek.</Text>}
          {list.data?.map((l) => (
            <UnstyledButton key={l.id} onClick={() => setSelected(l.id)} aria-current={l.id === selected ? 'true' : undefined}>
              <Paper withBorder radius="md" p="xs" className="pst-tile" style={l.id === selected ? { borderColor: 'var(--mantine-color-brand-5)', boxShadow: '0 0 0 1px var(--mantine-color-brand-5)' } : undefined}>
                <Text size="sm" fw={600} lineClamp={2}>{l.name}</Text>
                <Text size="xs" c="dimmed">{l.titles} Titel · {l.positions} Positionen{l.notes ? ` · ${l.notes} Vorbem.` : ''}</Text>
                {l.warning && <Text size="xs" c="orange.7" lineClamp={2} title={l.warning}>⚠ mögliche Dopplung</Text>}
              </Paper>
            </UnstyledButton>
          ))}
          {canEdit && (
            <form onSubmit={(e) => { e.preventDefault(); if (newName.trim()) create.mutate(); }}>
              <Group gap={4} wrap="nowrap">
                <TextInput size="xs" placeholder="Neue Bibliothek, z. B. Büro" value={newName} onChange={(e) => setNewName(e.currentTarget.value)} style={{ flex: 1 }} aria-label="Name der neuen Bibliothek" />
                <Button size="xs" type="submit" disabled={!newName.trim()} loading={create.isPending}>+</Button>
              </Group>
            </form>
          )}
        </Stack>
        <div style={{ flex: 1, minWidth: 0, width: narrow ? '100%' : undefined }}>
          {current ? (
            <LibraryEditor key={current.id} libraryId={current.id} warning={current.warning} canEdit={canEdit} onDelete={() => { if (window.confirm(`Bibliothek „${current.name}“ mit ${current.titles + current.positions + current.notes} Einträgen löschen? In Leistungsverzeichnisse übernommene Positionen bleiben erhalten.`)) remove.mutate(current.id); }} />
          ) : (
            list.data && list.data.length > 0 && <Text c="dimmed" size="sm">Bibliothek wählen.</Text>
          )}
        </div>
      </Group>
      {importing && (
        <GaebImport
          onClose={() => setImporting(false)}
          onDone={async (id) => { await refresh(); await queryClient.invalidateQueries({ queryKey: ['library', id] }); setSelected(id); }}
        />
      )}
    </Stack>
  );
}

/** A GAEB file as base64, in slices so a large file does not overflow the call stack. */
async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

type Planned = { file: File; base64: string; preview: ImportResult | null; error: string | null; action: Exclude<ImportAction, 'auto'>; done: ImportResult | null };

/** What each verdict proposes, and the choices the user has for one file. */
const actionLabel = (a: Exclude<ImportAction, 'auto'>, match: string | undefined): string =>
  a === 'create' ? 'als neue Bibliothek anlegen' : a === 'skip' ? 'nicht einlesen' : a === 'replace' ? `„${match}“ ersetzen` : `an „${match}“ anhängen`;

/**
 * Reading GAEB DA XML files (e.g. manufacturers' downloads) into libraries
 * (AP-79). Each file is first compared by content with the existing
 * libraries (AP-81); PST proposes what to do - skip the same texts, replace
 * with a newer version, a new library with a warning when unclear - and the
 * user may choose otherwise per file before anything is written.
 */
function GaebImport({ onClose, onDone }: {
  onClose: () => void;
  onDone: (libraryId: string) => Promise<void>;
}): JSX.Element {
  const [plan, setPlan] = useState<Planned[]>([]);
  const [busy, setBusy] = useState(false);
  const [finished, setFinished] = useState(false);
  const choose = async (files: FileList | File[] | null): Promise<void> => {
    if (!files?.length) return;
    setBusy(true);
    const next: Planned[] = [];
    for (const file of Array.from(files)) {
      if (file.size > 18_000_000) { next.push({ file, base64: '', preview: null, error: 'größer als 18 MB', action: 'skip', done: null }); continue; }
      const base64 = await fileToBase64(file);
      try {
        const preview = await libraryApi.importGaeb({ fileName: file.name, contentBase64: base64, action: 'auto', dryRun: true });
        next.push({ file, base64, preview, error: null, action: preview.action, done: null });
      } catch (e) {
        next.push({ file, base64, preview: null, error: errorText(e, 'nicht lesbar'), action: 'skip', done: null });
      }
    }
    setPlan(next);
    setFinished(false);
    setBusy(false);
  };
  const run = async (): Promise<void> => {
    setBusy(true);
    const out = [...plan];
    let last: string | null = null;
    for (const [i, p] of out.entries()) {
      if (!p.preview || p.action === 'skip') continue;
      try {
        const done = await libraryApi.importGaeb({ fileName: p.file.name, contentBase64: p.base64, action: p.action, libraryId: p.action === 'replace' || p.action === 'append' ? p.preview.match?.id ?? null : null });
        out[i] = { ...p, done };
        last = done.libraryId ?? last;
      } catch (e) {
        out[i] = { ...p, error: errorText(e, 'Einlesen fehlgeschlagen') };
      }
    }
    setPlan(out);
    setBusy(false);
    setFinished(true);
    if (last) await onDone(last);
  };
  return (
    <Modal opened onClose={onClose} title="GAEB-Dateien einlesen" size="xl">
      <Stack gap="sm">
        <Text size="sm">
          GAEB DA XML (z. B. .x81, .x82, .x83), etwa von einer Herstellerseite heruntergeladen. PST vergleicht jede Datei mit den vorhandenen Bibliotheken nach Inhalt – nicht nach Dateiname – und schlägt vor, was geschehen soll. Übernommen werden Gliederung, OZ, Kurz- und Langtext, Einheit, Positionsart (Bedarf, Wahl, Pauschal) und Vorbemerkungen; Preise nicht.
        </Text>
        {!finished && (
          <Stack gap={4}>
            <FileDrop accept={GAEB_ACCEPT} multiple disabled={busy} onFiles={(f) => void choose(f)} label={busy && plan.length === 0 ? 'wird geprüft …' : 'GAEB-Dateien hierher ziehen oder klicken'} hint="z. B. aus der Download-Leiste des Browsers" />
            <Group gap="xs"><PortalLinks />{plan.length > 0 && <Text size="sm">{plan.length} Datei{plan.length === 1 ? '' : 'en'} geprüft</Text>}</Group>
          </Stack>
        )}
        {plan.length > 0 && (
          <Table verticalSpacing={4}>
            <Table.Thead><Table.Tr><Table.Th>Datei</Table.Th><Table.Th>Befund</Table.Th><Table.Th w={260}>{finished ? 'Ergebnis' : 'Aktion'}</Table.Th></Table.Tr></Table.Thead>
            <Table.Tbody>
              {plan.map((p, i) => {
                const m = p.preview?.match?.name;
                const options: Exclude<ImportAction, 'auto'>[] = p.preview ? ['create', ...(p.preview.match ? ['replace', 'append'] as const : []), 'skip'] : ['skip'];
                return (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <Text size="sm">{p.file.name}</Text>
                      {p.preview && <Text size="xs" c="dimmed">{p.preview.name}{p.preview.issued ? ` · ${p.preview.issued}` : ''}</Text>}
                    </Table.Td>
                    <Table.Td>
                      {p.error ? <Text size="sm" c="red">{p.error}</Text> : (
                        <>
                          <Text size="sm" c={p.preview?.verdict === 'similar' ? 'orange.7' : undefined}>{p.preview?.reason}</Text>
                          <Text size="xs" c="dimmed">{p.preview?.report[0]}</Text>
                        </>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {finished ? (
                        <Text size="sm">{p.done ? `${IMPORT_OUTCOMES[p.done.outcome].label}${p.done.libraryName ? ` – ${p.done.libraryName}` : ''}` : p.error ? '—' : IMPORT_OUTCOMES.skipped.label}</Text>
                      ) : (
                        <Select size="xs" data={options.map((a) => ({ value: a, label: actionLabel(a, m) }))} value={p.action} disabled={!p.preview}
                          onChange={(v) => v && setPlan((all) => all.map((x, j) => (j === i ? { ...x, action: v as Exclude<ImportAction, 'auto'> } : x)))}
                          allowDeselect={false} comboboxProps={{ withinPortal: true }} />
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
        <Text size="xs" c="dimmed">Bibliotheken dienen der Verwendung in Ausschreibungen des Büros: Texte werden nur als Kopie in das LV eines Projekts übernommen, ganze Bibliotheken lassen sich nicht exportieren (R-21). Die Quelle steht bei jedem Eintrag.</Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{finished ? 'Schließen' : 'Abbrechen'}</Button>
          {!finished && <Button disabled={busy || !plan.some((p) => p.preview && p.action !== 'skip')} loading={busy && plan.length > 0} onClick={() => void run()}>Einlesen</Button>}
        </Group>
      </Stack>
    </Modal>
  );
}

/** Opens the text portals in a new tab (Weg A); the download is then dropped into PST. */
export function PortalLinks(): JSX.Element {
  return (
    <Group gap={4}>
      {TEXT_PORTALS.map((p) => (
        <Button key={p.url} size="xs" variant="subtle" component="a" href={p.url} target="_blank" rel="noopener noreferrer" title={p.hint}>{p.label} öffnen ↗</Button>
      ))}
    </Group>
  );
}

/**
 * A GAEB file straight into the open bill of quantities (Weg A): outline,
 * texts, units and the file's quantities; optionally into the libraries too.
 */
export function GaebIntoLv({ projectId, packageId, lvItems, onClose }: { projectId: string; packageId: string; lvItems: readonly TenderItem[]; onClose: () => void }): JSX.Element {
  // The file brings its own outline: by default at the end of the LV (AP-113).
  const [target, setTarget] = useState<string | null>(null);
  // Only the Administration changes the libraries (ADR 0020).
  const canManage = useCan('library.manage');
  const [alsoLibrary, setAlsoLibrary] = useState(canManage);
  const [file, setFile] = useState<File | null>(null);
  const run = useTenderMutation(projectId, packageId, async (f: File) => tenderApi.importGaebIntoLv(projectId, packageId, {
    fileName: f.name, contentBase64: await fileToBase64(f), targetTitleId: target, alsoLibrary,
  }));
  const tooBig = file !== null && file.size > 18_000_000;
  return (
    <Modal opened onClose={onClose} title="GAEB-Datei ins Leistungsverzeichnis" size="lg">
      <Stack gap="sm">
        <Text size="sm">Texte bei einem Portal auswählen und als GAEB XML (z. B. X81) herunterladen, dann die Datei hierher ziehen. Übernommen werden Gliederung mit Untertiteln, Kurz- und Langtext, Einheit, die Mengen der Datei, Bedarfs-, Wahl- und Pauschalpositionen und die Vorbemerkungen. Am Ende des LV bleibt die OZ der Datei, solange keine davon schon im LV steht; sonst wird neu nummeriert.</Text>
        <PortalLinks />
        {!run.isSuccess && (
          <>
            <FileDrop accept={GAEB_ACCEPT} disabled={run.isPending} onFiles={([f]) => f && setFile(f)} label={file ? `${file.name} (${Math.round(file.size / 1024)} KB)` : 'GAEB-Datei hierher ziehen oder klicken'} hint={file ? 'andere Datei: erneut ziehen oder klicken' : 'z. B. aus der Download-Leiste des Browsers'} />
            {tooBig && <Alert color="orange" py={6}>Die Datei ist größer als 18 MB.</Alert>}
            <TargetSelect lvItems={lvItems} value={target} onChange={setTarget} what="der Datei" />
            {canManage && <Checkbox label="zusätzlich als Textbibliothek speichern (mit Prüfung auf Dopplungen)" checked={alsoLibrary} onChange={(e) => setAlsoLibrary(e.currentTarget.checked)} />}
          </>
        )}
        {run.isError && <Alert color="red" py={6}>{errorText(run.error, 'Einlesen fehlgeschlagen.')}</Alert>}
        {run.isSuccess && (
          <Alert color="green" title={`${run.data.copied} Zeilen ins LV übernommen`}>
            {run.data.notes > 0 && <Text size="sm">darunter {run.data.notes} Vorbemerkung{run.data.notes === 1 ? '' : 'en'}</Text>}
            {run.data.library && <Text size="sm">Bibliothek: {run.data.library.outcome === 'skipped' ? 'nicht angelegt' : run.data.library.outcome === 'replaced' ? 'ersetzt' : 'angelegt'}{run.data.library.name ? ` – „${run.data.library.name}“` : ''} ({run.data.library.reason})</Text>}
            <Text size="xs" c="dimmed">{run.data.report.slice(1).join(' · ')}</Text>
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{run.isSuccess ? 'Schließen' : 'Abbrechen'}</Button>
          {!run.isSuccess && <Button disabled={!file || tooBig} loading={run.isPending} onClick={() => file && run.mutate(file)}>Ins LV übernehmen</Button>}
        </Group>
      </Stack>
    </Modal>
  );
}

const RUN_COLUMNS: LibraryColumn<ImportRun>[] = [
  { key: 'at', title: 'Zeit', defaultWidth: 150, render: (r) => <Text size="xs">{formatDateTime(r.at)}</Text> },
  { key: 'trigger', title: 'Weg', defaultWidth: 200, render: (r) => <Text size="xs">{IMPORT_TRIGGERS[r.trigger]}{r.userName ? ` · ${r.userName}` : ''}</Text> },
  { key: 'fileName', title: 'Datei', defaultWidth: 240, minWidth: 80, render: (r) => <Text size="sm">{r.fileName}</Text> },
  { key: 'outcome', title: 'Ergebnis', defaultWidth: 130, render: (r) => <Badge size="sm" variant="light" color={IMPORT_OUTCOMES[r.outcome].color}>{IMPORT_OUTCOMES[r.outcome].label}</Badge> },
  { key: 'libraryName', title: 'Bibliothek', defaultWidth: 200, render: (r) => <Text size="sm">{r.libraryName ?? '—'}</Text> },
  { key: 'reason', title: 'Befund', defaultWidth: 280, minWidth: 80, render: (r) => <Text size="xs" c="dimmed" lineClamp={2}>{r.reason}</Text> },
];

/** Every GAEB import, by hand or from the inbox folder (AP-81): for the administration. A log - looked at, never changed (AP-128). */
export function ImportLog(): JSX.Element {
  const runs = useQuery({ queryKey: ['import-runs'], queryFn: libraryApi.runs });
  if (runs.isPending) return <Center h={120}><Loader size="sm" /></Center>;
  if (runs.isError) return <Alert color="red">{errorText(runs.error, 'Das Protokoll konnte nicht geladen werden.')}</Alert>;
  if (runs.data.length === 0) return <Text size="sm" c="dimmed">Noch keine GAEB-Datei eingelesen.</Text>;
  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed">Die letzten {runs.data.length} Einlesevorgänge, neueste zuerst. Zeile öffnen für das ausführliche Protokoll.</Text>
      <LibraryTable<ImportRun>
        tableId="einleseprotokoll"
        label="Einleseprotokoll"
        columns={RUN_COLUMNS}
        rows={runs.data}
        rowKey={(r) => r.id}
        rowLabel={(r) => r.fileName}
        canManage={false}
        fields={(r) => [
          { key: 'at', label: 'Zeit', value: formatDateTime(r.at) },
          { key: 'trigger', label: 'Weg', value: `${IMPORT_TRIGGERS[r.trigger]}${r.userName ? ` · ${r.userName}` : ''}` },
          { key: 'fileName', label: 'Datei', value: r.fileName },
          { key: 'outcome', label: 'Ergebnis', value: IMPORT_OUTCOMES[r.outcome].label },
          { key: 'libraryName', label: 'Bibliothek', value: r.libraryName },
          { key: 'reason', label: 'Befund', value: r.reason, long: true },
          { key: 'report', label: 'Protokoll', value: r.report, long: true, mono: true },
        ]}
        lazyStep={100}
      />
    </Stack>
  );
}

const removeLabel = (item: LibraryItem): string =>
  item.kind === 'title' ? 'Titel mit allem darunter löschen' : item.kind === 'note' ? 'Vorbemerkung löschen' : 'Position löschen';

function LibraryEditor({ libraryId, warning, canEdit, onDelete }: { libraryId: string; warning: string | null; canEdit: boolean; onDelete: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const q = useQuery({ queryKey: ['library', libraryId], queryFn: () => libraryApi.get(libraryId) });
  const refresh = (): Promise<unknown> => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['library', libraryId] }),
    queryClient.invalidateQueries({ queryKey: ['libraries'] }),
  ]);
  const rename = useMutation({ mutationFn: (body: { name?: string; description?: string | null }) => libraryApi.update(libraryId, body), onSettled: refresh });
  const add = useMutation({ mutationFn: (body: Parameters<typeof libraryApi.addItem>[1]) => libraryApi.addItem(libraryId, body), onSettled: refresh });
  const update = useMutation({ mutationFn: ({ id, ...body }: { id: string } & LibraryItemInput) => libraryApi.updateItem(libraryId, id, body), onSettled: refresh });
  const remove = useMutation({ mutationFn: (id: string) => libraryApi.removeItem(libraryId, id), onSettled: refresh });
  const clearWarning = useMutation({ mutationFn: () => libraryApi.clearWarning(libraryId), onSettled: refresh });
  const error = rename.error ?? add.error ?? update.error ?? remove.error;

  if (q.isPending) return <Center h={120}><Loader size="sm" /></Center>;
  if (q.isError) return <Alert color="red">Die Bibliothek konnte nicht geladen werden.</Alert>;
  const d = q.data;
  const depth = depths(d.items);
  const columns: LibraryColumn<LibraryItem>[] = [
    { key: 'oz', title: LIBRARY_FIELDS.oz.label, hint: LIBRARY_FIELDS.oz.hint, defaultWidth: 80, render: (item) => <Text size="xs" c="dimmed" ff="monospace">{item.oz}</Text> },
    {
      key: 'shortText', title: LIBRARY_FIELDS.shortText.label, defaultWidth: 360, minWidth: 100,
      render: (item) => (
        <Group gap={4} wrap="nowrap" pl={`calc(${depth.get(item.id) ?? 0} * var(--mantine-spacing-md))`}>
          {item.kind === 'note' && <Text size="xs" c="dimmed" fs="italic" title={LIBRARY_FIELDS.note.hint} style={{ flexShrink: 0 }}>{LIBRARY_FIELDS.note.label}:</Text>}
          <Text size="sm" fw={item.kind === 'title' ? 600 : undefined} style={{ minWidth: 0, flex: 1 }}>{item.shortText}</Text>
          {item.longText && <Text size="xs" c="dimmed" title={LIBRARY_FIELDS.longText.label} style={{ flexShrink: 0 }}>¶</Text>}
        </Group>
      ),
    },
    { key: 'unit', title: LIBRARY_FIELDS.unit.label, defaultWidth: 70, render: (item) => item.kind === 'position' && <Text size="sm">{item.unit}</Text> },
    {
      key: 'positionType', title: LIBRARY_FIELDS.positionType.label, hint: LIBRARY_FIELDS.positionType.hint, defaultWidth: 140,
      render: (item) => item.kind === 'position' && item.positionType !== 'normal' && <Text size="sm">{POSITION_TYPES.find((t) => t.value === item.positionType)?.label}</Text>,
    },
    { key: 'source', title: LIBRARY_FIELDS.source.label, hint: LIBRARY_FIELDS.source.hint, defaultWidth: 200, render: (item) => <Text size="xs">{item.source}</Text> },
    { key: 'attribution', title: LIBRARY_FIELDS.attribution.label, hint: LIBRARY_FIELDS.attribution.hint, defaultWidth: 220, render: (item) => <Text size="xs" c="dimmed">{item.attribution}</Text> },
  ];

  return (
    <Stack gap="sm">
      {error && <Alert color="red" py={6}>{errorText(error)}</Alert>}
      {warning && (
        <Alert color="orange" py={6} title="Mögliche Dopplung">
          <Text size="sm">{warning}. PST hat die Datei im Zweifel als eigene Bibliothek angelegt; prüfen und bei Bedarf eine der beiden löschen.</Text>
          {canEdit && <Button size="compact-xs" variant="light" color="orange" mt={6} loading={clearWarning.isPending} onClick={() => clearWarning.mutate()}>Hinweis entfernen</Button>}
        </Alert>
      )}
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Stack gap={0} style={{ flex: 1, minWidth: 220 }}>
          <EditableText value={d.name} size="lg" bold onCommit={(name) => canEdit && name && rename.mutate({ name })} />
          <EditableText value={d.description ?? ''} size="xs" dimmed allowEmpty placeholder={canEdit ? 'Beschreibung ergänzen' : ' '} onCommit={(description) => canEdit && rename.mutate({ description: description || null })} />
        </Stack>
        {canEdit && (
          <Group gap="xs">
            <Button size="xs" variant="light" onClick={() => add.mutate({ kind: 'title', shortText: 'Neuer Titel' })}>+ Titel</Button>
            <Button size="xs" variant="light" onClick={() => add.mutate({ kind: 'position', shortText: 'Neue Position', unit: 'St' })}>+ Position</Button>
            <Button size="xs" variant="subtle" color="red" onClick={onDelete}>Bibliothek löschen …</Button>
          </Group>
        )}
      </Group>
      {d.items.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="lg">Noch leer. Titel gliedern die Bibliothek, Positionen stehen darunter.</Text>
      ) : (
        <LibraryTable<LibraryItem>
          tableId="ausschreibungstexte"
          label={d.name}
          columns={columns}
          rows={d.items}
          rowKey={(item) => item.id}
          rowLabel={(item) => item.shortText}
          rowStyle={(item) => (item.kind === 'title' ? { background: 'var(--mantine-color-default-hover)' } : undefined)}
          canManage={canEdit}
          fields={libraryItemFields}
          edit={async (item, changes) => { await update.mutateAsync({ id: item.id, ...(changes as LibraryItemInput) }); }}
          remove={{
            label: (item) => `${removeLabel(item)} …`,
            question: (item) => (item.kind === 'title' ? `Titel „${item.shortText}“ mit allem darunter löschen?` : `„${item.shortText}“ löschen?`),
            run: (item) => remove.mutate(item.id),
          }}
          extraMenu={(item, close) => (
            <>
              <Menu.Item onClick={() => { close(); add.mutate({ kind: 'position', shortText: 'Neue Position', unit: 'St', afterId: item.id }); }}>Position darunter einfügen</Menu.Item>
              <Menu.Item onClick={() => { close(); add.mutate({ kind: 'title', shortText: 'Neuer Titel', afterId: item.id }); }}>Titel darunter einfügen</Menu.Item>
            </>
          )}
          maxHeight={520}
          resetKey={libraryId}
        />
      )}
      <Text size="xs" c="dimmed">{canEdit ? 'Über das Menü einer Zeile (Rechtsklick oder ⋯) bearbeiten, löschen oder darunter einfügen. ' : ''}Keine Texte aus lizenzpflichtigen Katalogen (etwa STLB-Bau) ohne Lizenz eintragen.</Text>
      {d.importLog && (
        <details>
          <summary><Text span size="xs" c="dimmed" title={LIBRARY_FIELDS.importLog.hint}>{LIBRARY_FIELDS.importLog.label}</Text></summary>
          <Text size="xs" ff="monospace" style={{ whiteSpace: 'pre-wrap' }} mt={4}>{d.importLog}</Text>
        </details>
      )}
    </Stack>
  );
}

/**
 * Picking library entries for one bill of quantities. Ticking a title ticks
 * its positions; a ticked title comes along as a new title at the end.
 */
export function LibraryPicker({ projectId, packageId, lvItems, onClose }: { projectId: string; packageId: string; lvItems: readonly TenderItem[]; onClose: () => void }): JSX.Element {
  const list = useQuery({ queryKey: ['libraries'], queryFn: libraryApi.list });
  const [libraryId, setLibraryId] = useState<string | null>(null);
  const lib = useQuery({ queryKey: ['library', libraryId], queryFn: () => libraryApi.get(libraryId!), enabled: libraryId !== null });
  const [target, setTarget] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  const copy = useTenderMutation(projectId, packageId, () => tenderApi.copyFromLibrary(projectId, packageId, { libraryId: libraryId!, itemIds: [...picked], targetTitleId: target }));

  useEffect(() => {
    if (!libraryId && list.data && list.data.length > 0) setLibraryId(list.data[0]!.id);
  }, [list.data, libraryId]);
  useEffect(() => setPicked(new Set()), [libraryId]);

  const items = lib.data?.items ?? [];
  const depth = useMemo(() => depths(items), [items]);
  /** Everything below a title, titles of titles and notes (Vorbemerkungen, since AP-105) included. */
  const below = (id: string): LibraryItem[] => {
    const out: LibraryItem[] = [];
    const walk = (p: string): void => { for (const c of items) if (c.parentId === p) { out.push(c); if (c.kind === 'title') walk(c.id); } };
    walk(id);
    return out;
  };
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const shown = useMemo(() => {
    if (words.length === 0) return items;
    const hit = (i: LibraryItem): boolean => words.every((w) => [i.oz, i.shortText, i.longText, i.source].join(' ').toLowerCase().includes(w));
    return items.filter((i) => hit(i) || (i.kind === 'title' && below(i.id).some(hit)));
  }, [items, query]);
  const lazy = useLazyCount(shown.length, 150, `${libraryId ?? ''}|${query}`);
  const toggle = (item: LibraryItem, on: boolean): void => setPicked((s) => {
    const n = new Set(s);
    const ids = item.kind === 'title' ? [item.id, ...below(item.id).map((c) => c.id)] : [item.id];
    for (const id of ids) if (on) n.add(id); else n.delete(id);
    return n;
  });
  const positions = items.filter((i) => i.kind === 'position' && picked.has(i.id)).length;
  const newTitles = items.filter((i) => i.kind === 'title' && picked.has(i.id)).length;
  const notes = items.filter((i) => i.kind === 'note' && picked.has(i.id)).length;

  return (
    <Modal opened onClose={onClose} title="Aus Textbibliothek übernehmen" size="xl">
      <Stack gap="sm">
        {list.data?.length === 0 && <Text size="sm" c="dimmed">Noch keine Textbibliothek. Unter „Verwaltung › Bibliotheken › Ausschreibungstexte“ oder über „Textbibliotheken …“ im Leistungsverzeichnis anlegen.</Text>}
        {list.data && list.data.length > 0 && (
          <Group gap="sm" align="flex-end">
            <Select label="Bibliothek" data={list.data.map((l) => ({ value: l.id, label: `${l.name} (${l.positions})` }))} value={libraryId} onChange={setLibraryId} allowDeselect={false} w={260} comboboxProps={{ withinPortal: true }} />
            <TextInput label="Suchen" placeholder="Kurztext, Langtext, Quelle" value={query} onChange={(e) => setQuery(e.currentTarget.value)} style={{ flex: 1 }} data-autofocus />
          </Group>
        )}
        {lib.isPending && libraryId && <Center h={80}><Loader size="sm" /></Center>}
        {lib.data && shown.length === 0 && <Text size="sm" c="dimmed">Keine passenden Einträge.</Text>}
        {shown.length > 0 && (
          <Table.ScrollContainer minWidth={600} maxHeight={420}>
            <Table verticalSpacing={2} highlightOnHover stickyHeader>
              <Table.Tbody>
                {shown.slice(0, lazy.shown).map((i) => (
                  <Table.Tr key={i.id} style={i.kind === 'title' ? { background: 'var(--mantine-color-default-hover)' } : undefined}>
                    <Table.Td w={30}>
                      <Checkbox size="xs" aria-label={`${i.shortText} wählen`} checked={picked.has(i.id)} onChange={(e) => toggle(i, e.currentTarget.checked)} />
                    </Table.Td>
                    <Table.Td w={70}><Text size="xs" c="dimmed" ff="monospace">{i.oz}</Text></Table.Td>
                    <Table.Td pl={`calc(${depth.get(i.id) ?? 0} * var(--mantine-spacing-md))`}>
                      <Tooltip label={i.longText ?? 'ohne Langtext'} multiline w={360} disabled={i.kind === 'title'} openDelay={300} style={{ whiteSpace: 'pre-line' }}>
                        <Text size="sm" fw={i.kind === 'title' ? 600 : undefined} fs={i.kind === 'note' ? 'italic' : undefined} c={i.kind === 'note' ? 'dimmed' : undefined}>
                          {i.kind === 'note' ? `${LIBRARY_FIELDS.note.label}: ` : ''}{i.shortText}
                        </Text>
                      </Tooltip>
                      {i.kind === 'position' && (i.source || i.attribution) && <Text size="xs" c="dimmed">{[i.source, i.attribution].filter(Boolean).join(' · ')}</Text>}
                    </Table.Td>
                    <Table.Td w={70}><Text size="sm">{i.unit}</Text></Table.Td>
                    <Table.Td w={70}>{i.kind === 'position' && i.positionType !== 'normal' && <Badge size="xs" variant="light" color="gray">{POSITION_TYPES.find((t) => t.value === i.positionType)?.short}</Badge>}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {lazy.more}
          </Table.ScrollContainer>
        )}
        <TargetSelect lvItems={lvItems} value={target} onChange={setTarget} what="der Bibliothek" />
        <Text size="xs" c="dimmed">Übernommen werden Kurztext, Langtext, Einheit, Positionsart und Herkunft als Kopie; Mengen und Schätzpreise werden danach im LV eingetragen. Untertitel der Bibliothek bleiben Untertitel; Vorbemerkungen kommen als eigene Zeilen mit.</Text>
        {copy.isError && <Alert color="red" py={6}>{errorText(copy.error)}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Abbrechen</Button>
          <Button disabled={picked.size === 0 || !libraryId} loading={copy.isPending} onClick={() => copy.mutate(undefined, { onSuccess: onClose })}>
            {positions} Positionen{newTitles ? `, ${newTitles} Titel` : ''}{notes ? `, ${notes} Vorbemerkungen` : ''} übernehmen
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Where copied rows go in the LV (AP-113): "Ende des LV" puts titles as new
 * titles at the end, a chosen title takes them as its subtitles. Single
 * positions go below the chosen title, else below the last one.
 */
function TargetSelect({ lvItems, value, onChange, what }: { lvItems: readonly TenderItem[]; value: string | null; onChange: (v: string | null) => void; what: string }): JSX.Element | null {
  const titles = lvItems.filter((i) => i.kind === 'title');
  if (titles.length === 0) return null;
  const depth = depths(lvItems);
  return (
    <Select
      label="Einfügen"
      description={value
        ? `Titel ${what} werden Untertitel des gewählten Titels, einzelne Positionen kommen in ihn.`
        : `Titel ${what} kommen als neue Titel ans Ende, einzelne Positionen in den letzten Titel.`}
      data={[
        { value: '', label: 'am Ende des LV' },
        ...titles.map((t) => ({ value: t.id, label: `${'· '.repeat(depth.get(t.id) ?? 0)}unter ${t.oz} ${t.shortText}` })),
      ]}
      value={value ?? ''}
      onChange={(v) => onChange(v ? v : null)}
      allowDeselect={false}
      comboboxProps={{ withinPortal: true }}
    />
  );
}
